import type { JSX } from "react";
import { useMemo } from "react";
import { useAnalyticsApp } from "@/features/analytics/context";
import type { VastHistoryRow } from "../types";
import {
  buildChannelLookup,
  resolveChannelName,
  resolvePlatformName,
  resolveVastKind,
} from "../utils";
import { formatDate, formatNumber } from "./format";
import { SectionTable } from "./SectionTable";

export const VastsPanel = ({
  rows,
  page,
  pageSize,
  total,
  onPageChange,
}: {
  rows: VastHistoryRow[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (next: number) => void;
}): JSX.Element => {
  const { availableChannels } = useAnalyticsApp();
  const channelLookup = useMemo(
    () => buildChannelLookup(availableChannels),
    [availableChannels],
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-3">
      <SectionTable
        headers={["When", "IP", "UA", "Country", "Platform", "Channel", "Kind"]}
        rows={rows.map((row) => [
          formatDate(row.createdAt),
          row.ip,
          row.ua ? row.ua.slice(0, 40) : null,
          row.country,
          resolvePlatformName(row.platform),
          resolveChannelName(channelLookup, row.channelId),
          resolveVastKind(row.kind),
        ])}
        emptyLabel="No VAST history."
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-(--text-secondary)">
          Page {page} of {totalPages} • {formatNumber(total)} total
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-table-action"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn-table-action"
            disabled={page >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
